import React, { useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import GTM_ACCOUNTS from '../data/gtmAccounts';
import {
  GTM_USE_CASES, GTM_SECTOR_UC, GTM_COMPETITORS, GTM_FLAGS,
  GTM_OWNERS, GTM_OWNER_ROLES, GTM_COUNTRIES, GTM_REGIONS, GTM_SECTORS, GTM_STATUSES
} from '../data/gtmConstants';
import { useGTMAccounts } from '../hooks/useEntityQueries';
import { QUERY_KEYS } from '../queryClient';

const OWNER_COLORS = { Ajay: '#0d4f8b', Gilbert: '#5b21b6', Nick: '#065f46', Partha: '#92400e', Attif: '#be123c' };
const OWNER_BG = { Ajay: '#dbeafe', Gilbert: '#ede9fe', Nick: '#d1fae5', Partha: '#fef3c7', Attif: '#ffe4e6' };
const STATUS_STYLES = {
  prospect: { color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' },
  contacted: { color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' },
  meeting: { color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
  proposal: { color: '#d97706', bg: '#fef3c7', border: '#fcd34d' },
  poc: { color: '#0891b2', bg: '#cffafe', border: '#67e8f9' },
  closed: { color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
  lost: { color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
};
const SECTOR_COLORS = { Banking: '#1e40af', Telecom: '#7c2d12', Manufacturing: '#166534', Government: '#581c87', Conglomerate: '#0f766e', Regulator: '#991b1b' };
const SECTOR_BG = { Banking: '#dbeafe', Telecom: '#ffedd5', Manufacturing: '#dcfce7', Government: '#f3e8ff', Conglomerate: '#ccfbf1', Regulator: '#fee2e2' };

function Badge({ label, color, bg, border, style }) {
  return <span style={{ display: 'inline-flex', fontSize: '.6rem', fontWeight: 600, padding: '.08rem .3rem', borderRadius: 4, color, background: bg, border: `1px solid ${border || color}25`, whiteSpace: 'nowrap', ...style }}>{label}</span>;
}

function GTMHub() {
  const queryClient = useQueryClient();
  const { data: rawAccounts, isLoading: loading, isError } = useGTMAccounts();

  const accounts = useMemo(() => {
    if (isError) return GTM_ACCOUNTS;
    return (rawAccounts || []).map(a => ({
      ...a,
      useCases: typeof a.UseCases === 'string' ? JSON.parse(a.UseCases || '[]') : (a.UseCases || []),
      competitors: typeof a.Competitors === 'string' ? JSON.parse(a.Competitors || '[]') : (a.Competitors || []),
      keyPeople: a.keyPeople || [],
      warmContacts: a.warmContacts || [],
    }));
  }, [rawAccounts, isError]);

  const loadAccounts = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gtmAccounts });
  }, [queryClient]);

  const addNewAccount = useCallback(async () => {
    const acc = { AccountId: 'acc_' + Date.now(), Name: 'New Account', Country: 'Singapore', Region: 'APAC', Priority: 'Strategic', Sector: 'Banking', Owner: 'Nick', Status: 'prospect', Notes: '', UseCases: '[]', Competitors: '[]' };
    try {
      const created = await api.createGTMAccount(acc);
      loadAccounts();
      setModalAccount({ ...created, useCases: [], competitors: [], keyPeople: [], warmContacts: [] });
      setView('accounts');
    } catch (err) {
      console.error('Failed to create account:', err);
    }
  }, [loadAccounts]);
  const [view, setView] = useState('dashboard');
  const [searchQ, setSearchQ] = useState('');
  const [fRegion, setFRegion] = useState('all');
  const [fCountry, setFCountry] = useState('all');
  const [fSector, setFSector] = useState('all');
  const [fOwner, setFOwner] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [modalAccount, setModalAccount] = useState(null);
  const [ucInput, setUcInput] = useState('');
  const [compInput, setCompInput] = useState('');
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [showAddWarm, setShowAddWarm] = useState(false);
  const [newPerson, setNewPerson] = useState({ name: '', title: '', contact: '' });
  const [newWarm, setNewWarm] = useState({ name: '', relationship: '', intro_via: '' });

  // Normalize account fields (DB returns PascalCase, seed data uses camelCase)
  const norm = useCallback((a) => ({
    id: a.Id || a.id,
    accountId: a.AccountId || a.accountId || a.id,
    name: a.Name || a.name || '',
    country: a.Country || a.country || '',
    region: a.Region || a.region || '',
    priority: a.Priority || a.priority || 'Strategic',
    sector: a.Sector || a.sector || '',
    owner: a.Owner || a.owner || '',
    status: a.Status || a.status || 'prospect',
    notes: a.Notes || a.notes || '',
    useCases: a.useCases || [],
    competitors: a.competitors || [],
    keyPeople: a.keyPeople || [],
    warmContacts: a.warmContacts || [],
    keyPeopleCount: a.KeyPeopleCount || 0,
    warmContactsCount: a.WarmContactsCount || 0,
  }), []);

  const accts = useMemo(() => accounts.map(norm), [accounts, norm]);

  const filtered = useMemo(() => {
    return accts.filter(a => {
      if (fRegion !== 'all' && a.region !== fRegion) return false;
      if (fCountry !== 'all' && a.country !== fCountry) return false;
      if (fSector !== 'all' && a.sector !== fSector) return false;
      if (fOwner !== 'all' && a.owner !== fOwner) return false;
      if (fStatus !== 'all' && a.status !== fStatus) return false;
      if (searchQ) {
        const s = searchQ.toLowerCase();
        return (a.name.toLowerCase().includes(s) || a.country.toLowerCase().includes(s) ||
          a.sector.toLowerCase().includes(s) || a.owner.toLowerCase().includes(s) ||
          (a.notes || '').toLowerCase().includes(s) || (a.useCases || []).join(' ').toLowerCase().includes(s));
      }
      return true;
    });
  }, [accts, fRegion, fCountry, fSector, fOwner, fStatus, searchQ]);

  const updateAccount = async (id, updates) => {
    // Optimistic update via cache: patch the cached GTM accounts list immediately.
    const updated = { ...modalAccount, ...updates };
    queryClient.setQueryData(QUERY_KEYS.gtmAccounts, (prev) =>
      Array.isArray(prev)
        ? prev.map(a => (a.Id || a.id) === id ? { ...a, ...updates } : a)
        : prev
    );
    if (modalAccount) setModalAccount(updated);
    // Save to API in background; invalidate so the next render reconciles with server truth.
    try {
      await api.updateGTMAccount(id, {
        Name: updated.name || updated.Name,
        Country: updated.country || updated.Country,
        Region: updated.region || updated.Region,
        Priority: updated.priority || updated.Priority,
        Sector: updated.sector || updated.Sector,
        Owner: updated.owner || updated.Owner,
        Status: updated.status || updated.Status,
        Notes: updated.notes || updated.Notes || '',
        UseCases: JSON.stringify(updated.useCases || []),
        Competitors: JSON.stringify(updated.competitors || []),
      });
      loadAccounts();
    } catch (err) { console.error('Failed to update account:', err); }
  };

  const deleteAccount = async (id) => {
    try {
      await api.deleteGTMAccount(id);
      await loadAccounts();
      setModalAccount(null);
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const pill = (label, active, onClick, count) => (
    <button key={label} onClick={onClick} style={{ background: active ? '#dbeafe' : '#fff', border: `1px solid ${active ? '#2563eb' : '#e5e7eb'}`, borderRadius: 6, padding: '.22rem .5rem', fontSize: '.68rem', fontWeight: active ? 600 : 500, color: active ? '#2563eb' : '#6b7280', cursor: 'pointer' }}>
      {label}{count !== undefined && <span style={{ fontSize: '.55rem', opacity: .5, marginLeft: '.15rem' }}>{count}</span>}
    </button>
  );

  const barRow = (label, count, max, color) => (
    <div key={label}>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '.2rem 0', fontSize: '.72rem' }}>
        <span style={{ color: '#6b7280' }}>{label}</span>
        <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '.7rem' }}>{count}</span>
      </div>
      <div style={{ height: 4, background: '#f0f2f5', borderRadius: 2, marginBottom: '.25rem', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 2, width: `${max > 0 ? Math.round(count / max * 100) : 0}%`, background: color }} />
      </div>
    </div>
  );

  const statCard = (label, value, sub, color) => (
    <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '.75rem', boxShadow: '0 1px 3px rgba(0,0,0,.06)', borderTop: `2px solid ${color}` }}>
      <div style={{ fontSize: '.58rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: '#9ca3af' }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 800 }}>{value}</div>
      <div style={{ fontSize: '.58rem', color: '#6b7280' }}>{sub}</div>
    </div>
  );

  const inputStyle = { width: '100%', background: '#f0f2f5', border: '1px solid #e5e7eb', borderRadius: 6, padding: '.32rem .55rem', fontSize: '.78rem', outline: 'none' };

  // ─── DASHBOARD ───
  const renderDashboard = () => {
    const total = accts.length;
    const countries = new Set(accts.map(a => a.country)).size;
    const strategic = accts.filter(a => a.priority === 'Strategic').length;
    const withUC = accts.filter(a => a.useCases?.length > 0).length;
    const withPeople = accts.filter(a => a.keyPeople?.length > 0).length;
    const withComp = accts.filter(a => a.competitors?.length > 0).length;
    const active = accts.filter(a => !['prospect', 'lost'].includes(a.status)).length;
    const byCountry = {}; accts.forEach(a => { byCountry[a.country] = (byCountry[a.country] || 0) + 1; });
    const bySector = {}; accts.forEach(a => { bySector[a.sector] = (bySector[a.sector] || 0) + 1; });
    const byOwner = {}; accts.forEach(a => { byOwner[a.owner] = (byOwner[a.owner] || 0) + 1; });
    const byStatus = {}; accts.forEach(a => { byStatus[a.status || 'prospect'] = (byStatus[a.status || 'prospect'] || 0) + 1; });

    return (
      <div>
        <div style={{ marginBottom: '1.2rem' }}>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800 }}>Go-To-Market <span style={{ color: '#059669' }}>Strategy Hub</span></h1>
          <p style={{ color: '#6b7280', fontSize: '.82rem', maxWidth: 650, marginTop: '.3rem' }}>Target account mapping across APAC, Middle East, and EU — with use-cases, key contacts, competitors, and warm relationships.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '.5rem', marginBottom: '1.5rem' }}>
          {statCard('Accounts', total, `${countries} countries`, '#059669')}
          {statCard('Strategic', strategic, `${total - strategic} opportunistic`, '#2563eb')}
          {statCard('Use-Cases Set', withUC, `${total > 0 ? Math.round(withUC / total * 100) : 0}% coverage`, '#7c3aed')}
          {statCard('Key People', withPeople, `${total > 0 ? Math.round(withPeople / total * 100) : 0}% mapped`, '#d97706')}
          {statCard('Competitors', withComp, `${total > 0 ? Math.round(withComp / total * 100) : 0}% identified`, '#0891b2')}
          {statCard('Active', active, 'In pipeline', '#db2777')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '.75rem' }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <h3 style={{ fontSize: '.82rem', fontWeight: 700, marginBottom: '.6rem' }}>By Country</h3>
            {Object.entries(byCountry).sort((a, b) => b[1] - a[1]).map(([c, n]) => barRow(`${GTM_FLAGS[c] || ''} ${c}`, n, total, '#059669'))}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <h3 style={{ fontSize: '.82rem', fontWeight: 700, marginBottom: '.6rem' }}>By Sector</h3>
            {Object.entries(bySector).sort((a, b) => b[1] - a[1]).map(([s, n]) => barRow(s, n, total, '#2563eb'))}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <h3 style={{ fontSize: '.82rem', fontWeight: 700, marginBottom: '.6rem' }}>By Owner</h3>
            {Object.entries(byOwner).sort((a, b) => b[1] - a[1]).map(([o, n]) => barRow(o, n, total, OWNER_COLORS[o] || '#6b7280'))}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <h3 style={{ fontSize: '.82rem', fontWeight: 700, marginBottom: '.6rem' }}>Pipeline Status</h3>
            {GTM_STATUSES.map(s => barRow(s, byStatus[s] || 0, total, STATUS_STYLES[s]?.color || '#6b7280'))}
          </div>
        </div>
      </div>
    );
  };

  // ─── ACCOUNTS ───
  const renderAccounts = () => {
    const regionCounts = {}; accts.forEach(a => { regionCounts[a.region] = (regionCounts[a.region] || 0) + 1; });
    const countryCounts = {}; accts.forEach(a => { if (fRegion === 'all' || a.region === fRegion) countryCounts[a.country] = (countryCounts[a.country] || 0) + 1; });
    const sectorCounts = {}; accts.forEach(a => { sectorCounts[a.sector] = (sectorCounts[a.sector] || 0) + 1; });
    const ownerCounts = {}; accts.forEach(a => { ownerCounts[a.owner] = (ownerCounts[a.owner] || 0) + 1; });
    const statusCounts = {}; accts.forEach(a => { statusCounts[a.status || 'prospect'] = (statusCounts[a.status || 'prospect'] || 0) + 1; });

    return (
      <div>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: '1rem' }}>Target Accounts</h1>
        {[
          ['Region', fRegion, (v) => { setFRegion(v); setFCountry('all'); }, GTM_REGIONS, regionCounts],
          ['Country', fCountry, setFCountry, GTM_COUNTRIES.filter(c => fRegion === 'all' || accts.some(a => a.country === c && a.region === fRegion)), countryCounts],
          ['Sector', fSector, setFSector, GTM_SECTORS, sectorCounts],
          ['Owner', fOwner, setFOwner, GTM_OWNERS, ownerCounts],
        ].map(([label, current, setter, options, counts]) => (
          <div key={label} style={{ display: 'flex', flexWrap: 'wrap', gap: '.25rem', marginBottom: '.35rem' }}>
            <div style={{ fontSize: '.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#9ca3af', width: '100%', marginBottom: '.05rem' }}>{label}</div>
            {pill('All', current === 'all', () => setter('all'))}
            {options.map(o => pill(label === 'Country' ? `${GTM_FLAGS[o] || ''} ${o}` : o, current === o, () => setter(o), counts[o]))}
          </div>
        ))}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.25rem', marginBottom: '.6rem' }}>
          <div style={{ fontSize: '.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#9ca3af', width: '100%', marginBottom: '.05rem' }}>Status</div>
          {pill('All', fStatus === 'all', () => setFStatus('all'))}
          {GTM_STATUSES.map(s => pill(s.charAt(0).toUpperCase() + s.slice(1), fStatus === s, () => setFStatus(s), statusCounts[s]))}
        </div>
        <div style={{ fontSize: '.68rem', color: '#9ca3af', margin: '.6rem 0 .4rem' }}>Showing {filtered.length} of {accts.length} accounts</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '.6rem' }}>
          {filtered.map(a => (
            <div key={a.id} onClick={async () => {
              const acc = { ...a };
              try {
                const kp = await api.getGTMKeyPeople(a.accountId);
                acc.keyPeople = kp.map(p => ({ ...p, dbId: p.Id }));
              } catch { acc.keyPeople = []; }
              try {
                const wc = await api.getGTMWarmContacts(a.accountId);
                acc.warmContacts = wc.map(p => ({ ...p, dbId: p.Id }));
              } catch { acc.warmContacts = []; }
              setModalAccount(acc);
            }} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '.9rem 1rem', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.4rem' }}>
                <div style={{ fontSize: '.88rem', fontWeight: 700 }}>{a.name}</div>
                <Badge label={a.status || 'prospect'} color={STATUS_STYLES[a.status]?.color} bg={STATUS_STYLES[a.status]?.bg} border={STATUS_STYLES[a.status]?.border} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.25rem', marginBottom: '.35rem' }}>
                <Badge label={a.priority} color={a.priority === 'Strategic' ? '#059669' : '#d97706'} bg={a.priority === 'Strategic' ? 'rgba(5,150,105,.08)' : 'rgba(217,119,6,.08)'} />
                <Badge label={a.sector} color={SECTOR_COLORS[a.sector]} bg={SECTOR_BG[a.sector]} />
                <Badge label={a.owner} color={OWNER_COLORS[a.owner]} bg={OWNER_BG[a.owner]} />
                <span style={{ fontSize: '.6rem', color: '#9ca3af' }}>{GTM_FLAGS[a.country] || ''} {a.country}</span>
              </div>
              {a.useCases?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: '.2rem' }}>
                  {a.useCases.slice(0, 3).map(u => <span key={u} style={{ fontSize: '.58rem', fontWeight: 600, padding: '.12rem .4rem', borderRadius: 5, background: 'rgba(37,99,235,.07)', color: '#2563eb', margin: '.1rem' }}>{u}</span>)}
                  {a.useCases.length > 3 && <span style={{ fontSize: '.58rem', padding: '.12rem .4rem', borderRadius: 5, background: 'rgba(37,99,235,.07)', color: '#2563eb', opacity: .5, margin: '.1rem' }}>+{a.useCases.length - 3}</span>}
                </div>
              )}
              <div style={{ display: 'flex', gap: '.4rem', marginTop: '.35rem', paddingTop: '.35rem', borderTop: '1px solid #e5e7eb', fontSize: '.6rem', color: '#9ca3af' }}>
                <span>{a.keyPeopleCount || 0} contacts</span><span>|</span><span>{(a.competitors || []).length} competitors</span><span>|</span><span>{a.warmContactsCount || 0} warm intros</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ─── TEAM ───
  const renderTeam = () => (
    <div>
      <h1 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: '1rem' }}>Team Ownership</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '.75rem' }}>
        {GTM_OWNERS.map(o => {
          const accs = accts.filter(a => a.owner === o);
          const sectors = {}; accs.forEach(a => { sectors[a.sector] = (sectors[a.sector] || 0) + 1; });
          const countryList = [...new Set(accs.map(a => a.country))];
          return (
            <div key={o} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
              <h3 style={{ fontSize: '.95rem', fontWeight: 800, marginBottom: '.1rem' }}><Badge label={o} color={OWNER_COLORS[o]} bg={OWNER_BG[o]} /></h3>
              <div style={{ fontSize: '.7rem', color: '#6b7280', marginBottom: '.6rem' }}>{GTM_OWNER_ROLES[o]}</div>
              {[['Total', accs.length], ['Strategic', accs.filter(a => a.priority === 'Strategic').length], ['Active Pipeline', accs.filter(a => !['prospect', 'lost'].includes(a.status)).length]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '.15rem 0', fontSize: '.72rem' }}>
                  <span style={{ color: '#6b7280' }}>{l}</span><span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop: '.4rem', paddingTop: '.4rem', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '.6rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '.2rem' }}>Sectors</div>
                {Object.entries(sectors).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
                  <div key={s} style={{ display: 'flex', justifyContent: 'space-between', padding: '.15rem 0', fontSize: '.72rem' }}>
                    <span style={{ color: '#6b7280' }}>{s}</span><span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{n}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '.4rem', display: 'flex', flexWrap: 'wrap', gap: '.15rem' }}>
                {countryList.map(c => <span key={c} style={{ fontSize: '.58rem', background: '#f0f2f5', border: '1px solid #e5e7eb', borderRadius: 4, padding: '.1rem .3rem', fontWeight: 600 }}>{GTM_FLAGS[c] || ''} {c}</span>)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─── PIPELINE ───
  const renderPipeline = () => {
    const grid = {};
    GTM_COUNTRIES.forEach(c => { grid[c] = {}; GTM_SECTORS.forEach(s => { grid[c][s] = accts.filter(a => a.country === c && a.sector === s).length; }); });
    const maxVal = Math.max(...GTM_COUNTRIES.flatMap(c => GTM_SECTORS.map(s => grid[c][s])), 1);
    const ucCounts = {}; accts.forEach(a => (a.useCases || []).forEach(u => { ucCounts[u] = (ucCounts[u] || 0) + 1; }));
    const compCounts = {}; accts.forEach(a => (a.competitors || []).forEach(c => { compCounts[c] = (compCounts[c] || 0) + 1; }));

    return (
      <div>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: '1rem' }}>Pipeline Map</h1>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.1rem', overflowX: 'auto', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
          <h3 style={{ fontSize: '.82rem', fontWeight: 700, marginBottom: '.6rem' }}>Account Density — Country x Sector</h3>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 3, fontSize: '.68rem' }}>
            <thead><tr>
              <th style={{ textAlign: 'left', padding: '.35rem', fontWeight: 700, color: '#9ca3af', fontSize: '.58rem', textTransform: 'uppercase' }}>Country</th>
              {GTM_SECTORS.map(s => <th key={s} style={{ textAlign: 'center', padding: '.35rem', fontWeight: 700, color: '#9ca3af', fontSize: '.58rem', textTransform: 'uppercase' }}>{s}</th>)}
              <th style={{ textAlign: 'center', padding: '.35rem', fontWeight: 700, color: '#9ca3af', fontSize: '.58rem' }}>Total</th>
            </tr></thead>
            <tbody>{GTM_COUNTRIES.map(c => {
              const total = GTM_SECTORS.reduce((s, sec) => s + grid[c][sec], 0);
              return (
                <tr key={c}>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap', padding: '.35rem' }}>{GTM_FLAGS[c] || ''} {c}</td>
                  {GTM_SECTORS.map(s => {
                    const v = grid[c][s]; const intensity = v > 0 ? Math.max(.08, v / maxVal) : 0;
                    return <td key={s} style={{ textAlign: 'center', padding: '.35rem', borderRadius: 4, fontWeight: v ? 700 : 400, color: v ? '#2563eb' : '#9ca3af', background: v ? `rgba(37,99,235,${intensity})` : 'transparent' }}>{v || '·'}</td>;
                  })}
                  <td style={{ textAlign: 'center', fontWeight: 800, color: '#059669', background: 'rgba(5,150,105,.07)', borderRadius: 4, padding: '.35rem' }}>{total}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '.75rem' }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <h3 style={{ fontSize: '.82rem', fontWeight: 700, marginBottom: '.6rem' }}>Use-Case Coverage</h3>
            {Object.entries(ucCounts).length > 0 ? Object.entries(ucCounts).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([u, n]) => (
              <div key={u} style={{ display: 'flex', justifyContent: 'space-between', padding: '.2rem 0', fontSize: '.72rem' }}><span style={{ color: '#6b7280' }}>{u}</span><span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{n}</span></div>
            )) : <div style={{ fontSize: '.72rem', color: '#9ca3af' }}>No use-cases assigned yet.</div>}
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.1rem', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
            <h3 style={{ fontSize: '.82rem', fontWeight: 700, marginBottom: '.6rem' }}>Competitor Landscape</h3>
            {Object.entries(compCounts).length > 0 ? Object.entries(compCounts).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([c, n]) => (
              <div key={c} style={{ display: 'flex', justifyContent: 'space-between', padding: '.2rem 0', fontSize: '.72rem' }}><span style={{ color: '#6b7280' }}>{c}</span><span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{n}</span></div>
            )) : <div style={{ fontSize: '.72rem', color: '#9ca3af' }}>No competitors mapped yet.</div>}
          </div>
        </div>
      </div>
    );
  };

  // ─── MODAL ───
  const renderModal = () => {
    if (!modalAccount) return null;
    const a = modalAccount;
    const sugUC = (GTM_SECTOR_UC[a.sector] || GTM_USE_CASES).filter(u => !(a.useCases || []).includes(u));
    const ucSuggestions = GTM_USE_CASES.filter(u => !(a.useCases || []).includes(u) && (!ucInput || u.toLowerCase().includes(ucInput.toLowerCase())));
    const compSuggestions = GTM_COMPETITORS.filter(c => !(a.competitors || []).includes(c) && (!compInput || c.toLowerCase().includes(compInput.toLowerCase())));

    const addUC = (uc) => { updateAccount(a.id, { useCases: [...(a.useCases || []), uc] }); setUcInput(''); };
    const rmUC = (uc) => { updateAccount(a.id, { useCases: (a.useCases || []).filter(u => u !== uc) }); };
    const addComp = (c) => { updateAccount(a.id, { competitors: [...(a.competitors || []), c] }); setCompInput(''); };
    const rmComp = (c) => { updateAccount(a.id, { competitors: (a.competitors || []).filter(x => x !== c) }); };
    const addPerson = async () => {
      if (!newPerson.name) return;
      try {
        await api.createGTMKeyPerson({ AccountId: a.accountId, Name: newPerson.name, Title: newPerson.title, Contact: newPerson.contact });
        const kp = await api.getGTMKeyPeople(a.accountId);
        setModalAccount(prev => ({ ...prev, keyPeople: kp.map(p => ({ ...p, dbId: p.Id })) }));
      } catch (err) { console.error('Add person failed:', err); }
      setNewPerson({ name: '', title: '', contact: '' }); setShowAddPerson(false);
    };
    const rmPerson = async (i) => {
      const person = (a.keyPeople || [])[i];
      if (!person) return;
      try {
        await api.deleteGTMKeyPerson(person.dbId || person.Id);
        const kp = await api.getGTMKeyPeople(a.accountId);
        setModalAccount(prev => ({ ...prev, keyPeople: kp.map(p => ({ ...p, dbId: p.Id })) }));
      } catch (err) { console.error('Remove person failed:', err); }
    };
    const addWarm = async () => {
      if (!newWarm.name) return;
      try {
        await api.createGTMWarmContact({ AccountId: a.accountId, Name: newWarm.name, Relationship: newWarm.relationship, IntroVia: newWarm.intro_via });
        const wc = await api.getGTMWarmContacts(a.accountId);
        setModalAccount(prev => ({ ...prev, warmContacts: wc.map(p => ({ ...p, dbId: p.Id })) }));
      } catch (err) { console.error('Add warm contact failed:', err); }
      setNewWarm({ name: '', relationship: '', intro_via: '' }); setShowAddWarm(false);
    };
    const rmWarm = async (i) => {
      const contact = (a.warmContacts || [])[i];
      if (!contact) return;
      try {
        await api.deleteGTMWarmContact(contact.dbId || contact.Id);
        const wc = await api.getGTMWarmContacts(a.accountId);
        setModalAccount(prev => ({ ...prev, warmContacts: wc.map(p => ({ ...p, dbId: p.Id })) }));
      } catch (err) { console.error('Remove warm contact failed:', err); }
    };

    const regionMap = { Singapore: 'APAC', Malaysia: 'APAC', Indonesia: 'APAC', India: 'APAC', Thailand: 'APAC', Vietnam: 'APAC', Philippines: 'APAC', 'Saudi Arabia': 'Middle East', UAE: 'Middle East', France: 'EU', Germany: 'EU', Italy: 'EU', Denmark: 'EU' };

    return (
      <div onClick={(e) => { if (e.target === e.currentTarget) { setModalAccount(null); setShowAddPerson(false); setShowAddWarm(false); } }} style={{ display: 'flex', position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 200, alignItems: 'flex-start', justifyContent: 'center', padding: '2rem', overflowY: 'auto', backdropFilter: 'blur(4px)' }}>
        <div style={{ background: '#fff', borderRadius: 18, maxWidth: 860, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,.12)', position: 'relative' }}>
          <button onClick={() => { setModalAccount(null); setShowAddPerson(false); setShowAddWarm(false); }} style={{ position: 'absolute', top: '.8rem', right: '.8rem', background: '#f0f2f5', border: '1px solid #e5e7eb', borderRadius: 7, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1 }}>✕</button>

          <div style={{ padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.5rem' }}>
              <Badge label={a.priority} color={a.priority === 'Strategic' ? '#059669' : '#d97706'} bg={a.priority === 'Strategic' ? 'rgba(5,150,105,.08)' : 'rgba(217,119,6,.08)'} />
              <Badge label={a.sector} color={SECTOR_COLORS[a.sector]} bg={SECTOR_BG[a.sector]} />
              <span style={{ fontSize: '.7rem', color: '#6b7280' }}>{GTM_FLAGS[a.country] || ''} {a.country} · {a.region}</span>
            </div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 800 }}>{a.name}</h2>
          </div>

          <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>
            {/* Details */}
            <div style={{ marginBottom: '1.3rem' }}>
              <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', marginBottom: '.5rem' }}>Account Details</div>
              <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem' }}><span style={{ fontSize: '.68rem', fontWeight: 600, color: '#6b7280', minWidth: 80 }}>Country</span><select style={inputStyle} value={a.country} onChange={e => updateAccount(a.id, { country: e.target.value, region: regionMap[e.target.value] || 'APAC' })}>{GTM_COUNTRIES.map(c => <option key={c} value={c}>{GTM_FLAGS[c]} {c}</option>)}</select></div>
              <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem' }}><span style={{ fontSize: '.68rem', fontWeight: 600, color: '#6b7280', minWidth: 80 }}>Sector</span><select style={inputStyle} value={a.sector} onChange={e => updateAccount(a.id, { sector: e.target.value })}>{GTM_SECTORS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem' }}><span style={{ fontSize: '.68rem', fontWeight: 600, color: '#6b7280', minWidth: 80 }}>Owner</span><select style={inputStyle} value={a.owner} onChange={e => updateAccount(a.id, { owner: e.target.value })}>{GTM_OWNERS.map(o => <option key={o} value={o}>{o} — {GTM_OWNER_ROLES[o]}</option>)}</select></div>
              <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem' }}><span style={{ fontSize: '.68rem', fontWeight: 600, color: '#6b7280', minWidth: 80 }}>Priority</span><select style={inputStyle} value={a.priority} onChange={e => updateAccount(a.id, { priority: e.target.value })}><option value="Strategic">Strategic</option><option value="Opportunistic">Opportunistic</option></select></div>
              <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem' }}><span style={{ fontSize: '.68rem', fontWeight: 600, color: '#6b7280', minWidth: 80 }}>Status</span><select style={inputStyle} value={a.status || 'prospect'} onChange={e => updateAccount(a.id, { status: e.target.value })}>{GTM_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}</select></div>
              <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.4rem' }}><span style={{ fontSize: '.68rem', fontWeight: 600, color: '#6b7280', minWidth: 80 }}>Notes</span><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={a.notes || ''} onChange={e => updateAccount(a.id, { notes: e.target.value })} placeholder="Account notes..." /></div>
            </div>

            {/* Use Cases */}
            <div style={{ marginBottom: '1.3rem' }}>
              <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', marginBottom: '.5rem' }}>Use Cases</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.15rem', marginBottom: '.4rem' }}>
                {(a.useCases || []).map(u => <span key={u} style={{ fontSize: '.58rem', fontWeight: 600, padding: '.12rem .4rem', borderRadius: 5, background: 'rgba(37,99,235,.07)', color: '#2563eb' }}>{u} <span onClick={() => rmUC(u)} style={{ cursor: 'pointer', opacity: .5, marginLeft: '.2rem' }}>✕</span></span>)}
              </div>
              <input style={inputStyle} value={ucInput} onChange={e => setUcInput(e.target.value)} placeholder="Type to add use-case..." onKeyDown={e => { if (e.key === 'Enter' && ucInput.trim()) addUC(ucInput.trim()); }} />
              {ucInput && ucSuggestions.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.08)', maxHeight: 150, overflowY: 'auto', marginTop: 2 }}>
                  {ucSuggestions.slice(0, 8).map(u => <div key={u} onClick={() => addUC(u)} style={{ padding: '.35rem .6rem', fontSize: '.72rem', cursor: 'pointer' }}>{u}</div>)}
                </div>
              )}
              <div style={{ marginTop: '.3rem', fontSize: '.6rem', color: '#9ca3af' }}>Suggested for {a.sector}:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.15rem', marginTop: '.15rem' }}>
                {sugUC.slice(0, 6).map(u => <button key={u} onClick={() => addUC(u)} style={{ fontSize: '.58rem', fontWeight: 600, padding: '.12rem .4rem', borderRadius: 5, background: 'rgba(37,99,235,.07)', color: '#2563eb', border: '1px solid rgba(37,99,235,.15)', cursor: 'pointer', opacity: .6 }}>{u} +</button>)}
              </div>
            </div>

            {/* Key People */}
            <div style={{ marginBottom: '1.3rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.5rem' }}>
                <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af' }}>Key People</div>
                <button onClick={() => setShowAddPerson(!showAddPerson)} style={{ background: 'rgba(37,99,235,.07)', border: '1px solid rgba(37,99,235,.2)', borderRadius: 5, padding: '.2rem .45rem', fontSize: '.62rem', fontWeight: 600, color: '#2563eb', cursor: 'pointer' }}>+ Add</button>
              </div>
              {(a.keyPeople || []).map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.4rem', background: '#f0f2f5', border: '1px solid #e5e7eb', borderRadius: 8, padding: '.4rem .6rem', marginBottom: '.3rem' }}>
                  <span style={{ fontSize: '.78rem', fontWeight: 600, flex: 1 }}>{p.name}</span>
                  <span style={{ fontSize: '.65rem', color: '#6b7280', flex: 1 }}>{p.title}</span>
                  <span style={{ fontSize: '.6rem', color: '#9ca3af', flex: 1 }}>{p.contact}</span>
                  <button onClick={() => rmPerson(i)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>✕</button>
                </div>
              ))}
              {showAddPerson && (
                <div style={{ display: 'flex', gap: '.3rem', marginTop: '.3rem' }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Name" value={newPerson.name} onChange={e => setNewPerson(p => ({ ...p, name: e.target.value }))} />
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Title" value={newPerson.title} onChange={e => setNewPerson(p => ({ ...p, title: e.target.value }))} />
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Contact" value={newPerson.contact} onChange={e => setNewPerson(p => ({ ...p, contact: e.target.value }))} />
                  <button onClick={addPerson} style={{ padding: '.3rem .5rem', fontSize: '.62rem', borderRadius: 5, fontWeight: 600, background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer' }}>Add</button>
                </div>
              )}
              {!(a.keyPeople || []).length && !showAddPerson && <div style={{ fontSize: '.7rem', color: '#9ca3af' }}>No contacts yet.</div>}
            </div>

            {/* Competitors */}
            <div style={{ marginBottom: '1.3rem' }}>
              <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', marginBottom: '.5rem' }}>Competitors</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.15rem', marginBottom: '.4rem' }}>
                {(a.competitors || []).map(c => <span key={c} style={{ fontSize: '.58rem', fontWeight: 500, padding: '.1rem .35rem', borderRadius: 4, background: 'rgba(220,38,38,.07)', color: '#dc2626' }}>{c} <span onClick={() => rmComp(c)} style={{ cursor: 'pointer', opacity: .5 }}>✕</span></span>)}
              </div>
              <input style={inputStyle} value={compInput} onChange={e => setCompInput(e.target.value)} placeholder="Type competitor name..." onKeyDown={e => { if (e.key === 'Enter' && compInput.trim()) addComp(compInput.trim()); }} />
              {compInput && compSuggestions.length > 0 && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.08)', maxHeight: 150, overflowY: 'auto', marginTop: 2 }}>
                  {compSuggestions.slice(0, 8).map(c => <div key={c} onClick={() => addComp(c)} style={{ padding: '.35rem .6rem', fontSize: '.72rem', cursor: 'pointer' }}>{c}</div>)}
                </div>
              )}
            </div>

            {/* Warm Contacts */}
            <div style={{ marginBottom: '1.3rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.5rem' }}>
                <div style={{ fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af' }}>Warm Contacts</div>
                <button onClick={() => setShowAddWarm(!showAddWarm)} style={{ background: 'rgba(37,99,235,.07)', border: '1px solid rgba(37,99,235,.2)', borderRadius: 5, padding: '.2rem .45rem', fontSize: '.62rem', fontWeight: 600, color: '#2563eb', cursor: 'pointer' }}>+ Add</button>
              </div>
              {(a.warmContacts || []).map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.4rem', background: '#f0f2f5', border: '1px solid #e5e7eb', borderRadius: 8, padding: '.4rem .6rem', marginBottom: '.3rem' }}>
                  <span style={{ fontSize: '.78rem', fontWeight: 600, flex: 1 }}>{p.name}</span>
                  <span style={{ fontSize: '.65rem', color: '#6b7280', flex: 1 }}>{p.relationship}</span>
                  <span style={{ fontSize: '.6rem', color: '#9ca3af', flex: 1 }}>{p.intro_via}</span>
                  <button onClick={() => rmWarm(i)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}>✕</button>
                </div>
              ))}
              {showAddWarm && (
                <div style={{ display: 'flex', gap: '.3rem', marginTop: '.3rem' }}>
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Name" value={newWarm.name} onChange={e => setNewWarm(p => ({ ...p, name: e.target.value }))} />
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Relationship" value={newWarm.relationship} onChange={e => setNewWarm(p => ({ ...p, relationship: e.target.value }))} />
                  <input style={{ ...inputStyle, flex: 1 }} placeholder="Intro via" value={newWarm.intro_via} onChange={e => setNewWarm(p => ({ ...p, intro_via: e.target.value }))} />
                  <button onClick={addWarm} style={{ padding: '.3rem .5rem', fontSize: '.62rem', borderRadius: 5, fontWeight: 600, background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer' }}>Add</button>
                </div>
              )}
              {!(a.warmContacts || []).length && !showAddWarm && <div style={{ fontSize: '.7rem', color: '#9ca3af' }}>No warm contacts yet.</div>}
            </div>

            {/* Delete */}
            <div style={{ marginTop: '1rem', paddingTop: '.8rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => { if (window.confirm(`Delete ${a.name}?`)) deleteAccount(a.id); }} style={{ background: '#fff', border: '1px solid rgba(220,38,38,.3)', borderRadius: 6, padding: '.3rem .6rem', fontSize: '.7rem', fontWeight: 600, color: '#dc2626', cursor: 'pointer' }}>Delete Account</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '.1rem', marginBottom: '1.5rem' }}>
        {[['dashboard', 'Dashboard'], ['accounts', 'Accounts'], ['team', 'Team'], ['pipeline', 'Pipeline']].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)} style={{ background: view === id ? 'rgba(37,99,235,.07)' : 'none', border: 'none', color: view === id ? '#2563eb' : '#6b7280', fontSize: '.76rem', fontWeight: view === id ? 600 : 500, padding: '.35rem .6rem', borderRadius: 6, cursor: 'pointer' }}>{label}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search accounts..." style={{ width: 220, background: '#f0f2f5', border: '1px solid #e5e7eb', borderRadius: 7, padding: '.4rem .7rem', fontSize: '.78rem', outline: 'none' }} />
          <button onClick={addNewAccount} style={{ background: '#2563eb', border: '1px solid #2563eb', borderRadius: 6, padding: '.3rem .65rem', fontSize: '.7rem', fontWeight: 600, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Account</button>
        </div>
      </div>
      {view === 'dashboard' && renderDashboard()}
      {view === 'accounts' && renderAccounts()}
      {view === 'team' && renderTeam()}
      {view === 'pipeline' && renderPipeline()}
      {renderModal()}
    </div>
  );
}

export default GTMHub;
